/**
 * Pure rules of the Role Management page (no Firestore, unit-tested).
 */

/** Roles the app itself relies on: they can never be deleted (the trash icon is hidden for all of them). */
export const PROTECTED_ROLES = ['admin', 'viewer', 'manager'];

/**
 * The users (profiles {role, email, name}) that hold `roleId`. A role that still has users cannot be
 * deleted (audit ADM-08): its users kept the old list for ever, and the next save of such a user in
 * User Management gave them an empty one.
 */
export const usersOfRole = (users, roleId) => (users || []).filter((u) => u && u.role === roleId);

/** "a@x, b@x and 3 more" - who still holds a role, for the refusal message. */
export const describeUsers = (users, max = 3) => {
    const names = (users || []).map((u) => u.email || u.name || u.id || '?');
    const shown = names.slice(0, max).join(', ');
    return names.length > max ? `${shown} and ${names.length - max} more` : shown;
};

/** Doc key of a role name: "Branch Supervisor" -> "branch_supervisor". */
export const roleIdOf = (name) => String(name || '').trim().toLowerCase().replace(/\s+/g, '_');

/**
 * The id a new role would get, or why it cannot be added. A name that maps to
 * an existing id - or that repeats an existing role's display name - is
 * refused: adding it used to REPLACE that role with an empty one, and Save then
 * pushed an empty permission list to all of its users (audit ADM-09).
 * Returns `{ id, error }`.
 */
export const newRoleIdFor = (name, roles) => {
    const trimmed = String(name || '').trim();
    const id = roleIdOf(trimmed);
    if (!id) return { id: null, error: 'Type a role name first.' };
    const existing = roles || {};
    const sameName = Object.entries(existing).find(([, r]) => String(r?.name || '').trim().toLowerCase() === trimmed.toLowerCase());
    if (existing[id] || sameName) {
        const [takenId, taken] = existing[id] ? [id, existing[id]] : sameName;
        return { id: null, error: `A role called "${taken?.name || takenId}" already exists. Pick another name, or select that role to change it.` };
    }
    return { id, error: null };
};

/**
 * The roles to edit from `settings/roles`: the stored roles when the document
 * has them, else the defaults (a first run: nothing stored yet, so saving the
 * defaults is the intended seed). Only a SUCCESSFUL read may call this - on a
 * failed read the page must not offer Save at all, or it would overwrite every
 * custom role with the defaults (audit ADM-10).
 */
// (an empty roles map counts as none stored: it would show no role at all - review 3 Oct 2026)
export const rolesFromDoc = ({ exists, data }, defaults) => (exists && data && data.roles && Object.keys(data.roles).length
    ? { ...data.roles } : { ...defaults });

// the change log lives in the roles document (one Firestore document: 1 MiB at most) - it keeps its newest entries up
// to this many characters and this many entries (review 3 Oct 2026: 200 entries of ~190 permissions could pass 1 MiB
// and then every save would fail)
export const HISTORY_MAX_CHARS = 300000;
export const HISTORY_MAX_ENTRIES = 500;

/** The change log as kept: the newest entries that fit (oldest first, as stored). */
export const capHistory = (entries, maxChars = HISTORY_MAX_CHARS, maxEntries = HISTORY_MAX_ENTRIES) => {
    const list = Array.isArray(entries) ? entries : [];
    const kept = [];
    let size = 2;
    for (let i = list.length - 1; i >= 0 && kept.length < maxEntries; i -= 1) {
        const n = JSON.stringify(list[i]).length + 1;
        if (size + n > maxChars) break;
        size += n;
        kept.push(list[i]);
    }
    return kept.reverse();
};

/**
 * What a save changes, role by role: [{ role, name, added, removed, note? }] between the roles as stored and as saved
 * (owner's permissions plan, 3 Oct 2026: the change log - "مين، إمتى، وإيه الصلاحيات اللي اتزادت أو اتشالت من أنهي
 * دور"). The admin holds everything and is left out; a role new to the database says so.
 */
export const roleChanges = (before, after) => {
    const out = [];
    for (const [id, r] of Object.entries(after || {})) {
        if (id === 'admin') continue;
        const was = new Set((before && before[id] && before[id].permissions) || []);
        const now = new Set((r && r.permissions) || []);
        const added = [...now].filter((k) => !was.has(k)).sort();
        const removed = [...was].filter((k) => !now.has(k)).sort();
        const isNew = !(before && before[id]);
        if (added.length || removed.length || isNew) out.push({ role: id, name: (r && r.name) || id, added, removed, ...(isNew ? { note: 'role created' } : {}) });
    }
    return out;
};
